import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Save, Plug, ShieldCheck, Loader2, Copy, Eye, EyeOff } from 'lucide-react';

const FIELD_DEFAULTS = {
  base_url: '',
  api_token: '',
  webhook_secret: '',
  provider_name: 'Game Provider',
  enabled: true,
  timeout_seconds: 30,
};

/**
 * Single upstream Games-Automation API config.
 *
 * One platform-wide HMAC-signed connection used by EVERY game provider entry.
 * Individual game provider rows (Juwa / VBlink / etc.) only carry code + name —
 * their actual create-account / load / redeem / balance calls are routed
 * through this central API.
 *
 * Rendered as a collapsible block at the top of the Game Providers page.
 */
export default function UpstreamGamesApiConfig() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(FIELD_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [info, setInfo] = useState({ is_token_set: false, masked: '' });
  const isAttachmentBackend = (form.integration_type || '').includes('backend');

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/game-provider-config');
      const cfg = res.data?.data || res.data || {};
      setForm({
        base_url: cfg.base_url || '',
        api_token: '',
        webhook_secret: '',
        provider_name: cfg.provider_name || 'Game Provider',
        enabled: cfg.enabled ?? true,
        timeout_seconds: cfg.timeout_seconds ?? 30,
        integration_type: cfg.integration_type || '',
        group_name: cfg.group_name || cfg.groupName || '',
      });
      setInfo({ is_token_set: !!cfg.is_token_set, masked: cfg.api_token || '' });
    } catch (err) {
      // 404 = no config yet — that's fine, fall through to empty form.
      if (err.response?.status !== 404) toast.error('Failed to load upstream config');
    } finally {
      setLoading(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        base_url: form.base_url.trim(),
        provider_name: form.provider_name.trim() || 'Game Provider',
        enabled: form.enabled,
        timeout_seconds: Number(form.timeout_seconds) || 30,
      };
      if (form.api_token.trim()) payload.api_token = form.api_token.trim();
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
      await api.put('/api/admin/game-provider-config', payload);
      toast.success('Upstream API saved');
      setForm(f => ({ ...f, api_token: '', webhook_secret: '' }));
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/api/admin/game-provider-config/test');
      const ok = res.data?.success || res.data?.ok;
      ok ? toast.success('Connection OK') : toast.error(res.data?.message || 'Connection failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const copyText = (val) => { if (val) { navigator.clipboard?.writeText(val); toast.success('Copied'); } };

  return (
    <div className="card p-5 mb-5" data-testid="upstream-api-config">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 text-left"
        data-testid="upstream-api-toggle"
      >
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.4)' }}>
          <Plug className="w-5 h-5 text-purple-300" />
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold flex items-center gap-2">
            {isAttachmentBackend ? 'Starlight Automation Backend' : 'Upstream Games Automation API'}
            {info.is_token_set && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><ShieldCheck className="w-3 h-3" /> Configured</span>}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {isAttachmentBackend
              ? `Using our automation backend attachments for group ${form.group_name || 'starlight'}. Game balances and provider actions route through that automation project.`
              : 'One HMAC-signed connection. Every game provider below routes through this single API.'}
          </p>
        </div>
        <span className="text-xs text-gray-400">{open ? 'Hide' : 'Configure'}</span>
      </button>

      {open && (
        loading ? (
          <div className="flex items-center justify-center py-6"><div className="spinner" /></div>
        ) : (
          <form onSubmit={save} className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Connection name</label>
                <Input value={form.provider_name} onChange={(e) => setForm(p => ({ ...p, provider_name: e.target.value }))}
                  className="bg-[#0B0E14] border-white/10" data-testid="gp-provider-name" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Request timeout (s)</label>
                <Input type="number" min={5} max={120} value={form.timeout_seconds}
                  onChange={(e) => setForm(p => ({ ...p, timeout_seconds: e.target.value }))}
                  className="bg-[#0B0E14] border-white/10" data-testid="gp-timeout" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 block">{isAttachmentBackend ? 'Automation backend URL' : 'API Base URL'}</label>
              <Input value={form.base_url} onChange={(e) => setForm(p => ({ ...p, base_url: e.target.value }))}
                placeholder="https://your-teamlobby-automation-api.example"
                className="bg-[#0B0E14] border-white/10" required data-testid="gp-base-url" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-400">{isAttachmentBackend ? 'Backend attachment secret' : 'API Key (X-API-Key)'}</label>
                {info.is_token_set && (
                  <span className="text-[10px] text-emerald-300 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Stored — blank to keep
                  </span>
                )}
              </div>
              {info.is_token_set && (
                <div className="flex items-center gap-2 text-xs mb-2 px-3 py-1.5 rounded bg-[#0B0E14] border border-white/10">
                  <span className="font-mono flex-1 truncate">{info.masked}</span>
                  <button type="button" onClick={() => copyText(info.masked)} className="p-1 hover:bg-white/5 rounded">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <Input type={showSecrets ? 'text' : 'password'} value={form.api_token}
                onChange={(e) => setForm(p => ({ ...p, api_token: e.target.value }))}
                placeholder={info.is_token_set ? '•••••••••• (leave blank to keep)' : 'gk_...'}
                className="bg-[#0B0E14] border-white/10 font-mono" autoComplete="new-password" data-testid="gp-api-key" />
            </div>

            {!isAttachmentBackend && <div>
              <label className="text-xs text-gray-400 mb-1 block">API Secret / Webhook Secret (HMAC signing)</label>
              <Input type={showSecrets ? 'text' : 'password'} value={form.webhook_secret}
                onChange={(e) => setForm(p => ({ ...p, webhook_secret: e.target.value }))}
                placeholder="gs_... (leave blank to keep current)"
                className="bg-[#0B0E14] border-white/10 font-mono" autoComplete="new-password" data-testid="gp-api-secret" />
              <button type="button" onClick={() => setShowSecrets(v => !v)} className="text-[10px] text-gray-400 hover:text-white inline-flex items-center gap-1 mt-1">
                {showSecrets ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showSecrets ? 'Hide' : 'Show'} typed values
              </button>
            </div>}

            {isAttachmentBackend && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-100">
                Active route: <span className="font-mono text-emerald-300">{form.base_url}/api/backend-attachments</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm(p => ({ ...p, enabled: v }))} data-testid="gp-enabled-switch" />
              <span className="text-xs">{form.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="gp-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save automation config
              </Button>
              <Button type="button" onClick={testConnection} disabled={testing || !info.is_token_set}
                variant="outline" className="border-white/10" data-testid="gp-test-connection">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                Test connection
              </Button>
            </div>

            <div className="text-[11px] text-gray-400 pt-3 border-t border-white/5 space-y-1">
              <p className="font-semibold text-gray-200">{isAttachmentBackend ? 'Current automation source:' : 'Webhook callback URL (give this to the upstream provider):'}</p>
              <code className="block px-2 py-1.5 rounded bg-[#0B0E14] border border-white/10 font-mono break-all">
                {isAttachmentBackend ? `${form.base_url}/api/backend-attachments/?groupName=${form.group_name || 'starlight'}` : `${window.location.origin}/api/webhooks/provider`}
              </code>
              <p className="opacity-80">
                {isAttachmentBackend
                  ? 'This uses our automation project backend, not the old admin HMAC provider API.'
                  : <>Outbound calls signed with HMAC-SHA256 using <code className="px-1 bg-white/5 rounded">timestamp.METHOD.path</code> (GET) / <code className="px-1 bg-white/5 rounded">timestamp.rawBody</code> (POST).</>}
              </p>
            </div>
          </form>
        )
      )}
    </div>
  );
}
